//  src/pages/admin/AlumniInformation.js
import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TableSortLabel,
  Avatar,
  Box,
  TextField,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  Checkbox,
  Divider,
} from '@mui/material';
import { Visibility, Edit, Delete } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import axios from '../../api/axios';
import './AlumniInformation.css';
import axiosInstance from '../../api/axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  getUndergraduateSchoolProgramMap,
  getPostgraduateSchoolProgramMap,
} from '../../utils/programOptions';

const schoolProgramMapBasicEducation = {
  'Grade School': [
    'Kindergarten',
    'Grade 1',
    'Grade 2',
    'Grade 3',
    'Grade 4',
    'Grade 5',
    'Grade 6',
  ],
  'Junior High School': ['Grade 7', 'Grade 8', 'Grade 9', 'Grade 10'],
  'Senior High School': ['Grade 11', 'Grade 12'],
  'High School': ['High School', 'Science High School'],
};

const API_BASE_URL = axiosInstance.defaults.baseURL;

function AlumniInformation() {
  // WebSocket connection
  useEffect(() => {
    const timeout = setTimeout(() => {
      const socket = new WebSocket(`ws://${window.location.hostname}:5001/ws`);

      socket.onopen = () => {
        // console.log('✅ WebSocket connected');
        socket.send("Hello server");
      };

      // socket.onmessage = (event) => {
      //   console.log('📩 Message from server:', event.data);
      // };

      socket.onclose = () => {
        console.log("❌ WebSocket disconnected");
      };
    }, 200); // delay connection

    return () => clearTimeout(timeout);
  }, []);

  const [alumniData, setAlumniData] = useState([]);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedAlumnus, setSelectedAlumnus] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const schoolProgramMapUndergraduate =
    getUndergraduateSchoolProgramMap(editData?.education?.tertiarySMU) || {};
  const schoolProgramMapPostgraduate =
    getPostgraduateSchoolProgramMap(editData?.education?.tertiarySMU) || {};
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [alumnusToDelete, setAlumnusToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewType, setPreviewType] = useState('image');
  const [recentFilter, setRecentFilter] = useState('');
  const [cvFilter, setCvFilter] = useState('');
  const [idFilter, setIdFilter] = useState('');
  const [signatureFilter, setSignatureFilter] = useState('');
  const [employmentFilter, setEmploymentFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const isSelected = (id) => selectedIds.has(id);
  // const [includeYear, setIncludeYear] = useState(false);

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Select/Deselect all *visible* rows (post-filter, post-sort)
  const handleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const allVisibleIds = filteredAndSortedAlumni.map((a) => a._id);
      const allSelected = allVisibleIds.every((id) => prev.has(id));
      if (allSelected) {
        // unselect all visible
        const next = new Set(prev);
        allVisibleIds.forEach((id) => next.delete(id));
        return next;
      } else {
        // select all visible (preserve any previously selected not visible)
        const next = new Set(prev);
        allVisibleIds.forEach((id) => next.add(id));
        return next;
      }
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkSetActive = async (active) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      await Promise.all(
        ids.map((id) =>
          axios.put(`/api/alumni/alumni/${id}/status`, { active }),
        ),
      );
      // update local state
      setAlumniData((prev) =>
        prev.map((a) => (selectedIds.has(a._id) ? { ...a, active } : a)),
      );
      clearSelection();
    } catch (e) {
      console.error('Bulk status update failed:', e);
      alert('Bulk status update failed.');
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const ok = window.confirm(
      `Delete ${ids.length} selected entr${
        ids.length > 1 ? 'ies' : 'y'
      }? This cannot be undone.`,
    );
    if (!ok) return;
    try {
      await Promise.all(
        ids.map((id) => axios.delete(`/api/alumni/alumni/${id}`)),
      );
      // refresh or just remove locally
      setAlumniData((prev) => prev.filter((a) => !selectedIds.has(a._id)));
      clearSelection();
    } catch (e) {
      console.error('Bulk delete failed:', e);
      alert('Bulk delete failed.');
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    const fetchAlumni = async () => {
      try {
        const res = await axios.get(`/api/alumni/alumni`);
        setAlumniData(res.data);
      } catch (err) {
        console.error("Error fetching alumni:", err);
      }
    };

    fetchAlumni();
  }, []);

  const handleToggleActive = async (alumnus) => {
    try {
      const id = alumnus._id;

      // Find the correct index in alumniData by _id
      const updated = [...alumniData];
      const idx = updated.findIndex((a) => a._id === id);
      if (idx === -1) return;

      const newStatus = !updated[idx].active;

      // Update backend
      await axios.put(`/api/alumni/alumni/${id}/status`, { active: newStatus });

      // Update frontend state only after success
      updated[idx] = { ...updated[idx], active: newStatus };
      setAlumniData(updated);
    } catch (error) {
      console.error("Failed to update status:", error);
      alert("Status update failed.");
    }
  };

  const handleView = (alumnus) => {
    setSelectedAlumnus(alumnus);
    setViewOpen(true);
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setSelectedAlumnus(null);
  };

  // EDIT MODAL
  const handleChange = (field) => (e) => {
    setEditData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleEducationChange = (field) => (e) => {
    if (field === "tertiarySMU") {
      // Program lists depend on graduation era, so clear stale selections
      // whenever the graduation year changes.
      setEditData((prev) => ({
        ...prev,
        schoolUndergraduate: "",
        programUndergraduate: "",
        schoolPostgraduate: "",
        programPostgraduate: "",
        education: { ...prev.education, tertiarySMU: e.target.value },
      }));
      return;
    }

    setEditData((prev) => ({
      ...prev,
      education: { ...prev.education, [field]: e.target.value },
    }));
  };

  const handleEmploymentChange = (field) => (e) => {
    setEditData((prev) => ({
      ...prev,
      employment: { ...prev.employment, [field]: e.target.value },
    }));
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditData(null);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditData((prev) => ({
          ...prev,
          photoPreview: reader.result,
          photoFile: file,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOpenEdit = (alumnus) => {
    setSelectedAlumnus(alumnus);

    setEditData({
      _id: alumnus._id,
      // Basic Info
      firstName: alumnus.firstName || "",
      middleName: alumnus.middleName || "",
      lastName: alumnus.lastName || "",
      suffix: alumnus.suffix || "",
      email: alumnus.email || "",
      contactNumber: alumnus.contactNumber || "",
      facebookAccount: alumnus.facebookAccount || "",
      password: "",
      confirmPassword: "",
      idNo: alumnus.idNo || "",

      // Father's Info
      fatherFirstName: alumnus.parents?.father?.firstName || "",
      fatherMiddleName: alumnus.parents?.father?.middleName || "",
      fatherLastName: alumnus.parents?.father?.lastName || "",
      fatherSuffix: alumnus.parents?.father?.suffix || "",

      // Mother's Info
      motherFirstName: alumnus.parents?.mother?.firstName || "",
      motherMiddleName: alumnus.parents?.mother?.middleName || "",
      motherLastName: alumnus.parents?.mother?.lastName || "",
      motherSuffix: alumnus.parents?.mother?.suffix || "",

      // Photo
      photoPreview: alumnus.photo?.filePath
        ? `${API_BASE_URL}/${alumnus.photo.filePath}`
        : "",

      // Education
      schoolUndergraduate: alumnus.schoolUndergraduate || "",
      programUndergraduate: alumnus.programUndergraduate || "",
      schoolPostgraduate: alumnus.schoolPostgraduate || "",
      programPostgraduate: alumnus.programPostgraduate || "",

      education: {
        recentGraduate: alumnus.smuHistory?.recentGraduate || "",
        elementarySMU: alumnus.smuHistory?.elementarySMU || "",
        juniorHighSMU: alumnus.smuHistory?.juniorHighSMU || "",
        highSchoolSMU: alumnus.smuHistory?.highSchoolSMU || "",
        hsOrShsSMU: alumnus.smuHistory?.hsOrShsSMU || "",
        seniorHighSMU: alumnus.smuHistory?.seniorHighSMU || "",
        strandInSMU: alumnus.smuHistory?.strandInSMU || "",
        tertiarySMU: alumnus.smuHistory?.tertiarySMU || "",
        nonGraduateSMU: alumnus.smuHistory?.nonGraduateSMU || "",
      },

      // Employment
      employment: {
        status: alumnus.employment?.employmentStatus || "",
        position: alumnus.employment?.currentWork || "",
        companyName: alumnus.employment?.companyName || "",
        companyAddress: alumnus.employment?.companyAddress || "",
        homeAddress: alumnus.address || "",
      },

      // Files
      cvFileName: alumnus.curriculumVitae?.fileName || "",
      cvFile: null,

      alumniIdFileName: alumnus.alumniIdApplication?.fileName || "",
      alumniIdFile: null,

      signaturePreview: alumnus.signature?.filePath
        ? `${API_BASE_URL}/${alumnus.signature.filePath}`
        : "",
      signatureFile: null,

      // Achievements
      achievements: alumnus.achievements || ["", "", "", "", ""],
    });

    setEditOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();

    const confirmed = window.confirm(
      `Are you sure you want to update the profile of ${editData.firstName} ${editData.lastName}?`
    );
    if (!confirmed) return;

    try {
      const formData = new FormData();

      formData.append("_id", editData._id);

      // Education fields
      formData.append("schoolUndergraduate", editData.schoolUndergraduate || "");
      formData.append(
        "programUndergraduate",
        editData.programUndergraduate || ""
      );
      formData.append("schoolPostgraduate", editData.schoolPostgraduate || "");
      formData.append(
        "programPostgraduate",
        editData.programPostgraduate || ""
      );

      const edu = editData.education || {};
      formData.append("recentGraduate", edu.recentGraduate || "");
      formData.append("elementarySMU", edu.elementarySMU || "");
      formData.append("juniorHighSMU", edu.juniorHighSMU || "");
      formData.append("highSchoolSMU", edu.highSchoolSMU || "");
      formData.append("hsOrShsSMU", edu.hsOrShsSMU || "");
      formData.append("seniorHighSMU", edu.seniorHighSMU || "");
      formData.append("strandInSMU", edu.strandInSMU || "");
      formData.append("tertiarySMU", edu.tertiarySMU || "");
      formData.append("nonGraduateSMU", edu.nonGraduateSMU || "");

      // Employment fields
      const emp = editData.employment || {};
      formData.append("employmentStatus", emp.status || "");
      formData.append("currentWork", emp.position || "");
      formData.append("companyName", emp.companyName || "");
      formData.append("companyAddress", emp.companyAddress || "");
      formData.append("address", emp.homeAddress || "");

      // Achievements
      formData.append(
        "achievements",
        JSON.stringify(editData.achievements || [])
      );

      // All other simple fields
      const excludeKeys = [
        "education",
        "employment",
        "achievements",
        "photoFile",
        "cvFile",
        "signatureFile",
        "alumniIdFile",
        "photoPreview",
        "signaturePreview",
        "schoolUndergraduate",
        "programUndergraduate",
        "schoolPostgraduate",
        "programPostgraduate",
      ];

      for (const key in editData) {
        if (!excludeKeys.includes(key)) {
          formData.append(key, editData[key]);
        }
      }

      // File uploads — only attach if new files are selected
      if (editData.photoFile) {
        formData.append("photo", editData.photoFile);
      }
      if (editData.cvFile) {
        formData.append("curriculumVitae", editData.cvFile);
      }
      if (editData.signatureFile) {
        formData.append("signature", editData.signatureFile);
      }
      if (editData.alumniIdFile) {
        formData.append("alumniIdApplication", editData.alumniIdFile);
      }

      // Send request
      await axios.put(`/api/alumni/alumni/${editData._id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Refresh data
      const res = await axios.get("/api/alumni/alumni");
      setAlumniData(res.data);
      alert(
        `Profile of ${editData.firstName} ${editData.lastName} updated successfully.`
      );
      setEditOpen(false);
    } catch (error) {
      console.error("Error updating alumni:", error);
      alert(
        `Failed to update ${editData.firstName} ${editData.lastName}'s profile.`
      );
    }
  };

  // EDIT MODAL ENDS

  // DELETE MODAL
  const handleOpenDelete = (alumnus) => {
    setAlumnusToDelete(alumnus);
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setAlumnusToDelete(null);
    setDeleteOpen(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`/api/alumni/alumni/${alumnusToDelete._id}`);

      const res = await axios.get("/api/alumni/alumni");
      setAlumniData(res.data);
      handleCloseDelete();
    } catch (err) {
      console.error("Error deleting alumni:", err);
    }
  };
  // DELETE MODAL ENDS

  const filteredAndSortedAlumni = alumniData
    .filter((alumnus) => {
      const fullName = `${alumnus?.firstName || ''} ${
        alumnus?.middleName || ''
      } ${alumnus?.lastName || ''} ${alumnus?.suffix || ''}`.toLowerCase();
      const email = (alumnus?.email || '').toLowerCase();
      // const idNo = (alumnus?.idNo || '').toLowerCase();
      const term = searchTerm.toLowerCase();

      const normalize = (v) => (v ?? '').toString().trim().toLowerCase();

      const yearGraduated = normalize(alumnus?.smuHistory?.tertiarySMU);

      const recentGradYear = /^\d{4}$/.test(
        (alumnus?.smuHistory?.recentGraduate ?? '').toString(),
      )
        ? normalize(alumnus?.smuHistory?.recentGraduate)
        : '';

      const matchSearch =
        fullName.includes(term) ||
        email.includes(term) ||
        // idNo.includes(term) ||
        yearGraduated.includes(term) ||
        (recentGradYear && recentGradYear.includes(term));

      const matchStatus =
        statusFilter === "active"
          ? alumnus.active
          : statusFilter === "inactive"
          ? !alumnus.active
          : true;

      const matchCV =
        cvFilter === ""
          ? true
          : cvFilter === "yes"
          ? !!alumnus.curriculumVitae?.fileName
          : !alumnus.curriculumVitae?.fileName;

      const matchID =
        idFilter === ""
          ? true
          : idFilter === "yes"
          ? !!alumnus.alumniIdApplication?.fileName
          : !alumnus.alumniIdApplication?.fileName;

      const matchSig =
        signatureFilter === ""
          ? true
          : signatureFilter === "yes"
          ? !!alumnus.signature?.filePath
          : !alumnus.signature?.filePath;

      const matchEmployment =
        employmentFilter === ""
          ? true
          : alumnus.employment?.employmentStatus === employmentFilter;

      const matchRecent = (() => {
        if (!recentFilter) return true;

        const createdAt = new Date(alumnus.createdAt);
        if (Number.isNaN(createdAt.getTime())) return true;

        const now = new Date();
        const diffMs = now - createdAt;
        const rf = recentFilter.trim().toLowerCase();

        // helpers for calendar ranges
        const startOfMonth = (d) =>
          new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
        const endOfMonth = (d) =>
          new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);

        const startOfCurrentMonth = startOfMonth(now);
        const endOfCurrentMonth = endOfMonth(now);

        // previous month
        const startOfLastMonth = startOfMonth(
          new Date(now.getFullYear(), now.getMonth() - 1, 1),
        );
        const endOfLastMonth = endOfMonth(
          new Date(now.getFullYear(), now.getMonth() - 1, 1),
        );

        // two months ago start → end of last month
        const startOfLast2Months = startOfMonth(
          new Date(now.getFullYear(), now.getMonth() - 2, 1),
        );
        const endOfLast2Months = endOfLastMonth; // defined above

        if (rf === 'today') {
          return createdAt.toDateString() === now.toDateString();
        }
        if (rf.includes('week')) {
          // "week" or "this week"
          return diffMs <= 7 * 24 * 60 * 60 * 1000;
        }
        if (rf.includes('this month')) {
          return (
            createdAt >= startOfCurrentMonth && createdAt <= endOfCurrentMonth
          );
        }
        if (rf.includes('last month')) {
          // ensure it's not matching "last 2 months"
          if (rf.includes('2')) {
            // handled below
          } else {
            return createdAt >= startOfLastMonth && createdAt <= endOfLastMonth;
          }
        }
        if (rf.includes('last 2 months')) {
          return (
            createdAt >= startOfLast2Months && createdAt <= endOfLast2Months
          );
        }

        // fallback to your previous rolling-30-day logic for "month"
        if (rf.includes('month')) {
          return diffMs <= 30 * 24 * 60 * 60 * 1000;
        }

        return true;
      })();

      return (
        matchSearch &&
        matchStatus &&
        matchCV &&
        matchID &&
        matchSig &&
        matchEmployment &&
        matchRecent
      );
    })

    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "idNo":
          aValue = a.idNo;
          bValue = b.idNo;
          break;
        case "name":
          aValue = `${a.firstName} ${a.lastName}`;
          bValue = `${b.firstName} ${b.lastName}`;
          break;
        case "yearGraduated":
          aValue = parseInt(a.smuHistory?.tertiarySMU || "0");
          bValue = parseInt(b.smuHistory?.tertiarySMU || "0");
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const exportToCSV = (rows, filename, headers) => {
    const csvRows = [headers.map((h) => `"${h.label}"`).join(",")];

    rows.forEach((row) => {
      const data = headers.map(({ key }) => {
        let value = row[key] || "";
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvRows.push(data.join(","));
    });

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.csv`;
    link.click();
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setRecentFilter("");
    setCvFilter("");
    setIdFilter("");
    setSignatureFilter("");
    setEmploymentFilter("");
  };

  const handleImport = async () => {
    if (!importFile) {
      alert("Please select a CSV file first.");
      return;
    }

    const formData = new FormData();
    formData.append("file", importFile);

    setImportLoading(true);
    try {
      const res = await axios.post("/api/alumni/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert(res.data.message);
      if (res.data.errors && res.data.errors.length > 0) {
        console.log("Import errors:", res.data.errors);
      }

      // Refresh data
      const fetchRes = await axios.get("/api/alumni/alumni");
      setAlumniData(fetchRes.data);

      setImportDialogOpen(false);
      setImportFile(null);
    } catch (err) {
      console.error("Import failed:", err);
      alert(err.response?.data?.message || "Import failed");
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div className="alumniContent">
      <Dialog
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
      >
        <DialogTitle>Select Download Option</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          {/* Add Year toggle */}
          {/* <FormControlLabel
            control={
              <Checkbox
                checked={includeYear}
                onChange={(e) => setIncludeYear(e.target.checked)}
              />
            }
            label="Add Graduated Year"
            sx={{ mb: 2, display: 'block', textAlign: 'left' }}
          /> */}

          {/* Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                const source = selectedIds.size
                  ? alumniData.filter((a) => selectedIds.has(a._id))
                  : alumniData;

                const yearbookData = source.map((a) => {
                  const base = {
                    idNo: a.idNo,
                    firstName: a.firstName,
                    middleName: a.middleName,
                    lastName: a.lastName,
                    suffix: a.suffix || '',
                    schoolLevel: a.school || a.education?.level || '',
                    program: a.program || '',
                    address: a.address || '',
                    birthday: formatDate(a.birthday),
                    yearGraduated: a.smuHistory?.tertiarySMU || '',
                    fatherName: `${a.parents?.father?.firstName || ''} ${
                      a.parents?.father?.middleName || ''
                    } ${a.parents?.father?.lastName || ''}`.trim(),
                    fatherSuffix: a.parents?.father?.suffix || '',
                    motherName: `${a.parents?.mother?.firstName || ''} ${
                      a.parents?.mother?.middleName || ''
                    } ${a.parents?.mother?.lastName || ''}`.trim(),
                    motherSuffix: a.parents?.mother?.suffix || '',
                    achievement1: a.achievements?.[0] || '',
                    achievement2: a.achievements?.[1] || '',
                    achievement3: a.achievements?.[2] || '',
                    achievement4: a.achievements?.[3] || '',
                    achievement5: a.achievements?.[4] || '',
                  };
                  return base;
                });

                const headers = [
                  { key: 'idNo', label: 'ID Number' },
                  { key: 'firstName', label: 'First Name' },
                  { key: 'middleName', label: 'Middle Name' },
                  { key: 'lastName', label: 'Last Name' },
                  { key: 'suffix', label: 'Suffix' },
                  { key: 'schoolLevel', label: 'School/College' },
                  { key: 'program', label: 'Program' },
                  { key: 'yearGraduated', label: 'Year Graduated' },
                  { key: 'address', label: 'Address' },
                  { key: 'birthday', label: 'Birthday' },
                  { key: 'fatherName', label: "Father's Name" },
                  { key: 'fatherSuffix', label: "Father's Suffix" },
                  { key: 'motherName', label: "Mother's Name" },
                  { key: 'motherSuffix', label: "Mother's Suffix" },
                  { key: 'achievement1', label: 'Achievement 1' },
                  { key: 'achievement2', label: 'Achievement 2' },
                  { key: 'achievement3', label: 'Achievement 3' },
                  { key: 'achievement4', label: 'Achievement 4' },
                  { key: 'achievement5', label: 'Achievement 5' },
                ];

                exportToCSV(yearbookData, 'Yearbook', headers);
                setDownloadDialogOpen(false);
              }}
            >
              YearBook
            </Button>

            <Button
              variant="outlined"
              onClick={() => {
                const source = selectedIds.size
                  ? alumniData.filter((a) => selectedIds.has(a._id))
                  : alumniData;

                const alumniIdData = source.map((a) => {
                  const base = {
                    firstName: a.firstName,
                    middleName: a.middleName,
                    lastName: a.lastName,
                    suffix: a.suffix || '',
                    schoolLevel: a.school || a.education?.level || '',
                    program: a.program || '',
                    yearGraduated: a.smuHistory?.tertiarySMU || '',
                    idNo: a.idNo || '',
                    address: a.address || '',
                    birthday: formatDate(a.birthday),
                  };
                  return base;
                });

                const headers = [
                  { key: 'firstName', label: 'First Name' },
                  { key: 'middleName', label: 'Middle Name' },
                  { key: 'lastName', label: 'Last Name' },
                  { key: 'suffix', label: 'Suffix' },
                  { key: 'schoolLevel', label: 'School/College' },
                  { key: 'program', label: 'Program' },
                  { key: 'yearGraduated', label: 'Year Graduated' },
                  { key: 'idNo', label: 'ID Number' },
                  { key: 'address', label: 'Address' },
                  { key: 'birthday', label: 'Birthday' },
                ];

                exportToCSV(alumniIdData, 'Alumni_ID', headers);
                setDownloadDialogOpen(false);
              }}
            >
              Alumni ID
            </Button>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDownloadDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="wrap"
        sx={{ mb: 2 }}
      >
        <TextField
          label="Search Alumni"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 200 }}
        />

        {[
          {
            label: "Recently Added",
            value: recentFilter,
            setter: setRecentFilter,
            options: [
              'Today',
              'This Week',
              'This Month',
              'Last Month',
              'Last 2 Months',
            ],
          },
          {
            label: "Signature",
            value: signatureFilter,
            setter: setSignatureFilter,
            options: ["Has Signature", "No Signature"],
          },
          {
            label: "Employment",
            value: employmentFilter,
            setter: setEmploymentFilter,
            options: [
              "Private Employee",
              "Government Employee",
              "Self-Employed",
              "N/A",
              "Other",
            ],
          },
        ].map((filter, idx) => (
          <FormControl key={idx} size="small" sx={{ minWidth: 160 }}>
            <InputLabel>{filter.label}</InputLabel>
            <Select
              label={filter.label}
              value={filter.value}
              onChange={(e) => filter.setter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {filter.options.map((opt) => (
                <MenuItem
                  key={opt}
                  value={
                    opt.toLowerCase().includes("yes")
                      ? "yes"
                      : opt.toLowerCase().includes("no")
                      ? "no"
                      : opt
                  }
                >
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}

        <Button
          variant="outlined"
          color="secondary"
          onClick={handleClearFilters}
          sx={{ height: "40px" }}
        >
          Clear
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => setDownloadDialogOpen(true)}
          startIcon={<DownloadIcon />}
          sx={{ height: "40px" }}
        >
          {selectedIds.size
            ? `Download Selected (${selectedIds.size})`
            : 'Download All Alumni'}
        </Button>

        <Button
          variant="contained"
          color="success"
          onClick={() => setImportDialogOpen(true)}
          startIcon={<FileUploadIcon />}
          sx={{ height: "40px" }}
        >
          Import Alumni
        </Button>

        {/* Bulk actions — only show when there are selections */}
        {selectedIds.size > 0 && (
          <>
            <Divider orientation="vertical" flexItem />
            <Stack direction="row" spacing={1}>
              <Tooltip title="Activate Selected">
                <IconButton color="success" onClick={() => bulkSetActive(true)}>
                  <CheckCircleIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Deactivate Selected">
                <IconButton
                  color="warning"
                  onClick={() => bulkSetActive(false)}
                >
                  <BlockIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete Selected">
                <IconButton color="error" onClick={bulkDelete}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </>
        )}
      </Stack>

      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "82.4vh",
          overflowY: "auto",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selectedIds.size > 0 &&
                    filteredAndSortedAlumni.some((a) =>
                      selectedIds.has(a._id),
                    ) &&
                    !filteredAndSortedAlumni.every((a) =>
                      selectedIds.has(a._id),
                    )
                  }
                  checked={
                    filteredAndSortedAlumni.length > 0 &&
                    filteredAndSortedAlumni.every((a) => selectedIds.has(a._id))
                  }
                  onChange={handleSelectAllVisible}
                  inputProps={{ 'aria-label': 'select all visible' }}
                />
              </TableCell>

              <TableCell
                sortDirection={sortBy === "idNo" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortBy === "idNo"}
                  direction={sortBy === "idNo" ? sortDirection : "asc"}
                  onClick={() => handleSort("idNo")}
                  hideSortIcon={false}
                  sx={{
                    fontWeight: "bold",
                    "& .MuiTableSortLabel-icon": {
                      opacity: 1,
                    },
                  }}
                >
                  <strong>ID No.</strong>
                </TableSortLabel>
              </TableCell>

              <TableCell
                sortDirection={sortBy === "name" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortBy === "name"}
                  direction={sortBy === "name" ? sortDirection : "asc"}
                  onClick={() => handleSort("name")}
                  hideSortIcon={false}
                  sx={{
                    fontWeight: "bold",
                    "& .MuiTableSortLabel-icon": {
                      opacity: 1,
                    },
                  }}
                >
                  <strong>Name and Email</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <strong>Program</strong>
              </TableCell>
              <TableCell
                sortDirection={
                  sortBy === "yearGraduated" ? sortDirection : false
                }
              >
                <TableSortLabel
                  active={sortBy === "yearGraduated"}
                  direction={sortBy === "yearGraduated" ? sortDirection : "asc"}
                  onClick={() => handleSort("yearGraduated")}
                  hideSortIcon={false}
                  sx={{
                    fontWeight: "bold",
                    "& .MuiTableSortLabel-icon": {
                      opacity: 1,
                    },
                  }}
                >
                  <strong>Year Graduated</strong>
                </TableSortLabel>
              </TableCell>

              <TableCell>
                <strong>
                  Activate Alumni{" "}
                  <Select
                    size="small"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    displayEmpty
                    sx={{ ml: 1, fontSize: "0.8rem" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </strong>
              </TableCell>
              <TableCell>
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedAlumni.map((alumnus, index) => (
              <TableRow key={alumnus._id}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected(alumnus._id)}
                    onChange={() => handleToggleSelect(alumnus._id)}
                    inputProps={{
                      'aria-label': `select ${alumnus.firstName} ${alumnus.lastName}`,
                    }}
                  />
                </TableCell>

                <TableCell>{alumnus.idNo}</TableCell>
                <TableCell>
                  <Typography style={{ fontWeight: "bold" }}>
                    {[
                      alumnus.firstName,
                      alumnus.middleName,
                      alumnus.lastName,
                      alumnus.suffix,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {alumnus.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  {alumnus.programUndergraduate}
                  {alumnus.programPostgraduate
                    ? ` / ${alumnus.programPostgraduate}`
                    : ""}
                </TableCell>
                <TableCell>
                  {alumnus.smuHistory?.tertiarySMU || "N/A"}
                </TableCell>
                <TableCell>
                  <Switch
                    checked={alumnus.active}
                    onChange={() => handleToggleActive(alumnus)}
                    color="primary"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    aria-label="View"
                    title="View"
                    size="small"
                    onClick={() => handleView(alumnus)}
                  >
                    <Visibility />
                  </IconButton>

                  <IconButton
                    aria-label="Edit"
                    title="Edit"
                    size="small"
                    onClick={() => handleOpenEdit(alumnus)}
                    sx={{ mx: 1 }}
                  >
                    <Edit />
                  </IconButton>

                  <IconButton
                    aria-label="Delete"
                    title="Delete"
                    size="small"
                    color="error"
                    onClick={() => handleOpenDelete(alumnus)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* VIEW MODAL */}
        <Dialog
          open={viewOpen}
          onClose={handleCloseView}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              fontWeight: "bold",
              background: "#272974",
              color: "white",
              textAlign: "center",
            }}
          >
            Alumni Profile
          </DialogTitle>

          <DialogContent dividers>
            <div className="alumni-container">
              {/* LEFT SIDE */}
              <div className="alumni-left">
                <Avatar
                  src={
                    selectedAlumnus?.photo?.filePath
                      ? `${API_BASE_URL}/${selectedAlumnus.photo.filePath}`
                      : ""
                  }
                  alt="Profile"
                  className="alumni-avatar"
                >
                  {[selectedAlumnus?.firstName, selectedAlumnus?.lastName]
                    .filter(Boolean)
                    .map((n) => n.charAt(0).toUpperCase())
                    .join("")}
                </Avatar>
                <h2 className="alumni-name">
                  {[
                    selectedAlumnus?.firstName,
                    selectedAlumnus?.middleName,
                    selectedAlumnus?.lastName,
                    selectedAlumnus?.suffix,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                </h2>
                <div>{selectedAlumnus?.email}</div>
                <div>{selectedAlumnus?.contactNumber}</div>
                <div>
                  <strong>ID Number:</strong> {selectedAlumnus?.idNo}
                </div>
                {selectedAlumnus?.facebookAccount && (
                  <a
                    href={selectedAlumnus.facebookAccount}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {selectedAlumnus.facebookAccount}
                  </a>
                )}
              </div>

              {/* RIGHT SIDE */}
              <div className="alumni-right">
                <div className="alumni-section">
                  <h2>Educational Background</h2>

                  <div className="alumni-row">
                    <div>
                      <strong>Undergraduate School:</strong>{" "}
                      {selectedAlumnus?.schoolUndergraduate || "N/A"}
                      <br />
                      <strong>Undergraduate Program:</strong>{" "}
                      {selectedAlumnus?.programUndergraduate || "N/A"}
                    </div>
                  </div>

                  <div className="alumni-row" style={{ marginTop: "10px" }}>
                    <div>
                      <strong>Postgraduate School:</strong>{" "}
                      {selectedAlumnus?.schoolPostgraduate || "N/A"}
                      <br />
                      <strong>Postgraduate Program:</strong>{" "}
                      {selectedAlumnus?.programPostgraduate || "N/A"}
                    </div>
                  </div>

                  <div sx={{ mb: 2, mt: 2 }}>
                    <strong>Recent Graduate:</strong>{" "}
                    {selectedAlumnus?.smuHistory?.recentGraduate?.toLowerCase() ===
                    "yes"
                      ? "Yes"
                      : selectedAlumnus?.smuHistory?.recentGraduate?.toLowerCase() ===
                        "no"
                      ? "No"
                      : "N/A"}
                  </div>

                  <h3>SMU Education History</h3>
                  <div className="alumni-row">
                    <div>
                      <strong>Grade School at SMU:</strong>{" "}
                      {selectedAlumnus?.smuHistory?.elementarySMU || "N/A"}
                    </div>
                    <div>
                      <strong>Junior High at SMU:</strong>{" "}
                      {selectedAlumnus?.smuHistory?.juniorHighSMU || "N/A"}
                    </div>
                  </div>
                  <div className="alumni-row">
                    <div>
                      <strong>High School at SMU:</strong>{" "}
                      {selectedAlumnus?.smuHistory?.highSchoolSMU || "N/A"}
                    </div>
                    <div>
                      <strong>High School or Science High School:</strong>{" "}
                      {selectedAlumnus?.smuHistory?.hsOrShsSMU || "N/A"}
                    </div>
                  </div>
                  <div className="alumni-row">
                    <div>
                      <strong>Senior High at SMU:</strong>{" "}
                      {selectedAlumnus?.smuHistory?.seniorHighSMU || "N/A"}
                    </div>
                    <div>
                      <strong>Senior High Strand:</strong>{" "}
                      {selectedAlumnus?.smuHistory?.strandInSMU || "N/A"}
                    </div>
                  </div>
                  <div>
                    <strong>Tertiary at SMU:</strong>{" "}
                    {selectedAlumnus?.smuHistory?.tertiarySMU || "N/A"}
                  </div>
                  <div>
                    <strong>Non-Graduate Attendance:</strong>{" "}
                    {selectedAlumnus?.smuHistory?.nonGraduateSMU || "N/A"}
                  </div>
                </div>

                <div className="alumni-section">
                  <h2>Employment Information</h2>
                  <div>
                    <strong>Status:</strong>{" "}
                    {selectedAlumnus?.employment?.employmentStatus || "N/A"}
                  </div>
                  <div>
                    <strong>Current Work:</strong>{" "}
                    {selectedAlumnus?.employment?.currentWork || "N/A"}
                  </div>
                  <div>
                    <strong>Company Name:</strong>{" "}
                    {selectedAlumnus?.employment?.companyName || "N/A"}
                  </div>
                  <div>
                    <strong>Company Address:</strong>{" "}
                    {selectedAlumnus?.employment?.companyAddress || "N/A"}
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={handleCloseView}
              variant="contained"
              color="primary"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* EDIT MODAL */}
        <Dialog
          open={editOpen}
          onClose={handleCloseEdit}
          maxWidth="md"
          fullWidth
        >
          {!editData ? null : (
            <>
              <DialogTitle
                sx={{
                  fontWeight: "bold",
                  background: "#272974",
                  color: "white",
                  textAlign: "center",
                }}
              >
                Edit Alumni Profile
              </DialogTitle>

              <DialogContent dividers>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 3,
                  }}
                >
                  {/* LEFT SIDE */}
                  <Box
                    sx={{
                      width: "35%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    {/* 👤 Profile Photo */}
                    {editData.photoPreview && (
                      <Box
                        component="img"
                        src={editData.photoPreview}
                        alt="Profile Photo"
                        onClick={() => {
                          setPreviewUrl(editData.photoPreview);
                          setPreviewType("image");
                          setPreviewOpen(true);
                        }}
                        sx={{
                          width: 120,
                          height: 120,
                          borderRadius: "50%",
                          border: "2px solid #272974",
                          cursor: "pointer",
                        }}
                      />
                    )}

                    <Button
                      variant="outlined"
                      component="label"
                      sx={{ color: "#272974", borderColor: "#272974" }}
                    >
                      Upload Photo
                      <input
                        hidden
                        accept="image/*"
                        type="file"
                        onChange={handlePhotoUpload}
                      />
                    </Button>

                    {/* 👤 Personal Details */}
                    <TextField
                      label="First Name"
                      fullWidth
                      value={editData.firstName}
                      onChange={handleChange("firstName")}
                    />
                    <TextField
                      label="Middle Name"
                      fullWidth
                      value={editData.middleName}
                      onChange={handleChange("middleName")}
                    />
                    <TextField
                      label="Last Name"
                      fullWidth
                      value={editData.lastName}
                      onChange={handleChange("lastName")}
                    />
                    <TextField
                      label="Suffix (e.g., Jr., III)"
                      fullWidth
                      value={editData.suffix}
                      onChange={handleChange("suffix")}
                    />
                    <TextField
                      label="Password"
                      fullWidth
                      value={editData.password}
                      onChange={handleChange("password")}
                    />
                    <TextField
                      label="ConfirmPassword"
                      fullWidth
                      value={editData.confirmPassword}
                      onChange={handleChange("confirmPassword")}
                    />
                    <TextField
                      label="Email"
                      fullWidth
                      value={editData.email}
                      onChange={handleChange("email")}
                    />
                    <TextField
                      label="Contact Number"
                      fullWidth
                      value={editData.contactNumber}
                      onChange={handleChange("contactNumber")}
                    />
                    <TextField
                      label="Facebook Link"
                      fullWidth
                      value={editData.facebookAccount}
                      onChange={handleChange("facebookAccount")}
                      sx={{ mb: "2em" }}
                    />

                    {/* 📄 Curriculum Vitae */}
                    <Typography sx={{ color: "#272974", fontWeight: "bold" }}>
                      Curriculum Vitae (Optional)
                    </Typography>
                    {editData.cvFileName && (
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => {
                          const fileUrl = `${API_BASE_URL}/uploads/users/cvs/${editData.cvFileName}`;
                          setPreviewUrl(fileUrl);
                          setPreviewType("pdf");
                          setPreviewOpen(true);
                        }}
                      >
                        {editData.cvFileName}
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      component="label"
                      sx={{ color: "#272974", borderColor: "#272974" }}
                    >
                      Update CV (PDF)
                      <input
                        hidden
                        type="file"
                        accept=".pdf"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setEditData((prev) => ({
                              ...prev,
                              cvFileName: file.name,
                              cvFile: file,
                            }));
                          }
                        }}
                      />
                    </Button>

                    {/* ✍️ Signature */}
                    {editData.signaturePreview && (
                      <Box
                        component="img"
                        src={editData.signaturePreview}
                        alt="Signature Preview"
                        onClick={() => {
                          setPreviewUrl(editData.signaturePreview);
                          setPreviewType("image");
                          setPreviewOpen(true);
                        }}
                        sx={{
                          width: 100,
                          height: "auto",
                          mt: 1,
                          border: "1px solid #ccc",
                          cursor: "pointer",
                        }}
                      />
                    )}
                    <Typography sx={{ color: "#272974", fontWeight: "bold" }}>
                      Signature(Optional)
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      sx={{ color: "#272974", borderColor: "#272974" }}
                    >
                      Upload Signature (PNG/JPG)
                      <input
                        hidden
                        type="file"
                        accept="image/png, image/jpeg"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setEditData((prev) => ({
                                ...prev,
                                signaturePreview: reader.result,
                                signatureFile: file,
                              }));
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </Button>

                    {/* 🆔 Alumni ID Application */}
                    <Typography sx={{ color: "#272974", fontWeight: "bold" }}>
                      Alumni ID Application (Optional)
                    </Typography>
                    {editData.alumniIdFileName && (
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => {
                          const fileUrl = `${API_BASE_URL}/uploads/users/alumniIdApplication/${editData.alumniIdFileName}`;
                          const ext = editData.alumniIdFileName
                            .split(".")
                            .pop()
                            .toLowerCase();
                          setPreviewUrl(fileUrl);
                          setPreviewType(
                            ["jpg", "jpeg", "png"].includes(ext)
                              ? "image"
                              : "pdf"
                          );
                          setPreviewOpen(true);
                        }}
                      >
                        {editData.alumniIdFileName}
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      component="label"
                      sx={{ color: "#272974", borderColor: "#272974" }}
                    >
                      Upload Application
                      <input
                        hidden
                        type="file"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setEditData((prev) => ({
                              ...prev,
                              alumniIdFileName: file.name,
                              alumniIdFile: file,
                            }));
                          }
                        }}
                      />
                    </Button>

                    {/* 👤 Parents Details */}
                    <Typography
                      sx={{ color: "#272974", fontWeight: "bold", mt: 2 }}
                    >
                      PARENTS DETAILS
                    </Typography>
                    {/* FATHER DETAILS */}
                    <TextField
                      label="Father's First Name"
                      fullWidth
                      value={editData.fatherFirstName}
                      onChange={handleChange("fatherFirstName")}
                    />
                    <TextField
                      label="Father's Middle Name"
                      fullWidth
                      value={editData.fatherMiddleName}
                      onChange={handleChange("fatherMiddleName")}
                    />
                    <TextField
                      label="Father's Last Name"
                      fullWidth
                      value={editData.fatherLastName}
                      onChange={handleChange("fatherLastName")}
                    />
                    <TextField
                      label="Father's Suffix (e.g., Jr., III)"
                      fullWidth
                      value={editData.fatherSuffix}
                      onChange={handleChange("fatherSuffix")}
                    />

                    {/* MOTHER DETAILS */}
                    <TextField
                      label="Mother's First Name"
                      fullWidth
                      value={editData.motherFirstName}
                      onChange={handleChange("motherFirstName")}
                      sx={{ mt: 2 }}
                    />
                    <TextField
                      label="Mother's MaidenMiddle Name"
                      fullWidth
                      value={editData.motherMiddleName}
                      onChange={handleChange("motherMiddleName")}
                    />
                    <TextField
                      label="Mother's Maiden Last Name"
                      fullWidth
                      value={editData.motherLastName}
                      onChange={handleChange("motherLastName")}
                    />
                    <TextField
                      label="Mother's Suffix (e.g., Jr., III)"
                      fullWidth
                      value={editData.motherSuffix}
                      onChange={handleChange("motherSuffix")}
                    />

                    {/* 🖼️ Preview Dialog */}
                    <Dialog
                      open={previewOpen}
                      onClose={() => setPreviewOpen(false)}
                      maxWidth="lg"
                      fullWidth
                    >
                      <Box sx={{ p: 2, textAlign: "center" }}>
                        {previewType === "image" ? (
                          <Box
                            component="img"
                            src={previewUrl}
                            alt="Preview"
                            sx={{
                              maxWidth: "90vw",
                              maxHeight: "90vh",
                              display: "block",
                              margin: "0 auto",
                              borderRadius: "4px",
                            }}
                          />
                        ) : (
                          <Box sx={{ width: "100%", height: "80vh" }}>
                            <iframe
                              src={previewUrl}
                              title="PDF Preview"
                              width="100%"
                              height="100%"
                              style={{ border: "none" }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Dialog>
                  </Box>

                  {/* RIGHT SIDE */}
                  <Box
                    sx={{
                      width: "60%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                    }}
                  >
                    {/* Educational Background */}
                    <Box
                      sx={{
                        background: "#f8f9fa",
                        padding: 2,
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="#272974"
                        gutterBottom
                      >
                        Educational Background
                      </Typography>

                      <FormControl
                        component="fieldset"
                        fullWidth
                        sx={{ mb: 2 }}
                      >
                        <FormLabel component="legend">
                          Recent Graduate
                        </FormLabel>
                        <RadioGroup
                          row
                          value={
                            editData.education.recentGraduate?.toLowerCase() ===
                            "no"
                              ? "No"
                              : "Yes"
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            setEditData((prev) => ({
                              ...prev,
                              education: {
                                ...prev.education,
                                recentGraduate: value,
                              },
                            }));
                          }}
                        >
                          <FormControlLabel
                            value="Yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="No"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>

                        {editData.education.recentGraduate?.toLowerCase() ===
                          "yes" && (
                          <TextField
                            label="Enter Year Graduated"
                            type="number"
                            fullWidth
                            sx={{ mt: 1 }}
                            value={editData.education.tertiarySMU || ""}
                            onChange={handleEducationChange("tertiarySMU")}
                            helperText="This determines which schools/programs are available below."
                          />
                        )}
                      </FormControl>

                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="#272974"
                      >
                        Undergraduate Program
                      </Typography>
                      {/* UNDERGRADUATE School/College AND COURSE/PROGRAM */}
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl
                            fullWidth
                            sx={{ mb: 2 }}
                            disabled={
                              Object.keys(schoolProgramMapUndergraduate)
                                .length === 0
                            }
                          >
                            <InputLabel id="school-ug-label">
                              {Object.keys(schoolProgramMapUndergraduate)
                                .length === 0
                                ? "Enter year graduated above first"
                                : "School/College"}
                            </InputLabel>
                            <Select
                              labelId="school-ug-label"
                              label={
                                Object.keys(schoolProgramMapUndergraduate)
                                  .length === 0
                                  ? "Enter year graduated above first"
                                  : "School/College"
                              }
                              value={editData.schoolUndergraduate}
                              onChange={(e) => {
                                setEditData((prev) => ({
                                  ...prev,
                                  schoolUndergraduate: e.target.value,
                                  programUndergraduate: "",
                                }));
                              }}
                              sx={{
                                backgroundColor: "white",
                                borderRadius: 1,
                              }}
                            >
                              {Object.keys(schoolProgramMapUndergraduate).map(
                                (school) => (
                                  <MenuItem key={school} value={school}>
                                    {school}
                                  </MenuItem>
                                ),
                              )}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl
                            fullWidth
                            sx={{ mb: 2 }}
                            disabled={!editData.schoolUndergraduate}
                          >
                            <InputLabel id="program-ug-label">
                              Course/Program
                            </InputLabel>
                            <Select
                              labelId="program-ug-label"
                              label="Course/Program"
                              value={editData.programUndergraduate}
                              onChange={(e) =>
                                setEditData((prev) => ({
                                  ...prev,
                                  programUndergraduate: e.target.value,
                                }))
                              }
                              sx={{
                                backgroundColor: "white",
                                borderRadius: 1,
                              }}
                            >
                              {(
                                schoolProgramMapUndergraduate[
                                  editData.schoolUndergraduate
                                ] || []
                              ).map((program) => (
                                <MenuItem key={program} value={program}>
                                  {program}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="#272974"
                        sx={{ mt: 1 }}
                      >
                        Postgraduate Program (Optional)
                      </Typography>
                      {/* POSTGRADUATE SCHOOL/LEVEL AND COURSE/PROGRAM */}
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel id="school-pg-label">
                              School/College
                            </InputLabel>
                            <Select
                              labelId="school-pg-label"
                              label="School/College"
                              value={editData.schoolPostgraduate}
                              onChange={(e) => {
                                setEditData((prev) => ({
                                  ...prev,
                                  schoolPostgraduate: e.target.value,
                                  programPostgraduate: "",
                                }));
                              }}
                              sx={{
                                backgroundColor: "white",
                                borderRadius: 1,
                              }}
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {Object.keys(schoolProgramMapPostgraduate).map(
                                (school) => (
                                  <MenuItem key={school} value={school}>
                                    {school}
                                  </MenuItem>
                                ),
                              )}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl
                            fullWidth
                            sx={{ mb: 2 }}
                            disabled={!editData.schoolPostgraduate}
                          >
                            <InputLabel id="program-pg-label">
                              Course/Program
                            </InputLabel>
                            <Select
                              labelId="program-pg-label"
                              label="Course/Program"
                              value={editData.programPostgraduate}
                              onChange={(e) =>
                                setEditData((prev) => ({
                                  ...prev,
                                  programPostgraduate: e.target.value,
                                }))
                              }
                              sx={{
                                backgroundColor: "white",
                                borderRadius: 1,
                              }}
                            >
                              {(
                                schoolProgramMapPostgraduate[
                                  editData.schoolPostgraduate
                                ] || []
                              ).map((program) => (
                                <MenuItem key={program} value={program}>
                                  {program}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        mt={2}
                        color="#272974"
                      >
                        SMU Education History
                      </Typography>

                      <Grid container spacing={2} mt={1}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="Elementary at SMU"
                            fullWidth
                            value={editData.education.elementarySMU}
                            onChange={handleEducationChange("elementarySMU")}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="Junior High at SMU"
                            fullWidth
                            value={editData.education.juniorHighSMU}
                            onChange={handleEducationChange("juniorHighSMU")}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="High School at SMU"
                            fullWidth
                            value={editData.education.highSchoolSMU}
                            onChange={handleEducationChange("highSchoolSMU")}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="High School or Science High School"
                            fullWidth
                            value={editData.education.hsOrShsSMU}
                            onChange={handleEducationChange("hsOrShsSMU")}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="Senior High at SMU"
                            fullWidth
                            value={editData.education.seniorHighSMU}
                            onChange={handleEducationChange("seniorHighSMU")}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="Senior High Strand"
                            fullWidth
                            value={editData.education.strandInSMU}
                            onChange={handleEducationChange("strandInSMU")}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="Tertiary at SMU"
                            fullWidth
                            value={editData.education.tertiarySMU}
                            onChange={handleEducationChange("tertiarySMU")}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="Non-Graduate Attendance"
                            fullWidth
                            value={editData.education.nonGraduateSMU}
                            onChange={handleEducationChange("nonGraduateSMU")}
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Employment Information */}
                    <Box
                      sx={{
                        background: "#f8f9fa",
                        padding: 2,
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="#272974"
                        gutterBottom
                      >
                        Employment Information
                      </Typography>

                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="employment-status-label">
                          Employment Status
                        </InputLabel>
                        <Select
                          labelId="employment-status-label"
                          id="employment-status"
                          value={editData.employment.status}
                          label="Employment Status"
                          onChange={handleEmploymentChange("status")}
                        >
                          <MenuItem value="">-- Select Status --</MenuItem>
                          <MenuItem value="Employed">Employed</MenuItem>
                          <MenuItem value="Government Employee">
                            Government Employee
                          </MenuItem>
                          <MenuItem value="Private Employee">
                            Private Employee
                          </MenuItem>
                          <MenuItem value="Self-Employed">
                            Self-Employed
                          </MenuItem>
                          <MenuItem value="N/A">N/A</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        label="Current Work"
                        fullWidth
                        value={editData.employment.position}
                        onChange={handleEmploymentChange("position")}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        label="Company Name"
                        fullWidth
                        value={editData.employment.companyName}
                        onChange={handleEmploymentChange("companyName")}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        label="Company Address"
                        fullWidth
                        value={editData.employment.companyAddress}
                        onChange={handleEmploymentChange("companyAddress")}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        label="Home Address"
                        placeholder="Town and Province/State and Country"
                        fullWidth
                        value={editData.employment.homeAddress}
                        onChange={handleEmploymentChange("homeAddress")}
                      />

                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        mt={2}
                        color="#272974"
                      >
                        Top 5 Achievements
                      </Typography>

                      {[0, 1, 2, 3, 4].map((idx) => (
                        <TextField
                          key={idx}
                          label={`Achievement ${idx + 1}`}
                          fullWidth
                          multiline
                          minRows={2}
                          sx={{ mb: 2 }}
                          value={editData.achievements?.[idx] || ""}
                          onChange={(e) =>
                            setEditData((prev) => {
                              const updated = [...(prev.achievements || [])];
                              updated[idx] = e.target.value;
                              return { ...prev, achievements: updated };
                            })
                          }
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </DialogContent>

              <DialogActions>
                <Button
                  className="save-button"
                  onClick={handleSaveEdit}
                  variant="contained"
                  color="success"
                >
                  Update
                </Button>
                <Button
                  onClick={handleCloseEdit}
                  variant="outlined"
                  color="inherit"
                >
                  Cancel
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* DELETE MODAL */}
        <Dialog open={deleteOpen} onClose={handleCloseDelete}>
          <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Delete</DialogTitle>
          <DialogContent dividers>
            Are you sure you want to delete{" "}
            <strong>
              {[alumnusToDelete?.firstName, alumnusToDelete?.lastName]
                .filter(Boolean)
                .join(" ")}
            </strong>
            ?
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDelete} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* IMPORT MODAL */}
        <Dialog
          open={importDialogOpen}
          onClose={() => !importLoading && setImportDialogOpen(false)}
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>Import Alumni Records</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Upload a CSV file containing alumni records. Required columns:{" "}
              <strong>firstName, lastName, email</strong>.
              <br />
              Optional columns: <strong>idNo, middleName, suffix, password, schoolUndergraduate, programUndergraduate, contactNumber, address, tertiarySMU</strong>.
            </Typography>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setImportFile(e.target.files[0])}
              style={{ marginTop: "10px" }}
            />
            {importFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {importFile.name}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setImportDialogOpen(false)}
              disabled={importLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              variant="contained"
              color="success"
              disabled={!importFile || importLoading}
            >
              {importLoading ? "Importing..." : "Import"}
            </Button>
          </DialogActions>
        </Dialog>
      </TableContainer>
    </div>
  );
}

export default AlumniInformation;
